<script setup lang="ts">
import {computed, onBeforeMount, ref} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'

import MembersList from './MembersList.vue'
import InvitesList from './InvitesList.vue'
import MembersListInviteDialog from './MembersListInviteDialog.vue'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useCheckOrgAccess } from '@/composables/useCheckAccess'

const store = useStore()
const route = useRoute()
const router = useRouter()

const orgId = route.params.org_id
const org = computed(() => store.getters['org/getCurrent'])
const members = computed(() => store.getters['org/getCurrentMembers'])
const projectMembers = computed(() => store.getters['org/getCurrentProjectMembers'])
const invites = computed(() => store.getters['org/getCurrentInvites'])

const isNewOrg = route.query.new === '1'
const activeTab = ref('organization')

const membersLength = computed(() => members.value.length)
const projectMembersLength = computed(() => projectMembers.value.length)
const totalMembersCount = computed(() => store.getters['org/getTotalMembersCount'])
const invitesLength = computed(() => invites.value.length)

const transformedProjectMembers = computed(() =>
  projectMembers.value.map((pm: any) => ({
    ...pm,
    role: 'project_member'
  }))
)

onBeforeMount(async () => {
  // Fetch org data if not already loaded for this specific org
  if (!org.value || String(org.value.organization_id) !== String(orgId)) {
    await store.dispatch('org/fetchOrg', orgId)
  }

  // If user is not a member of this org yet, redirect to overview to process invitation
  if (org.value && !org.value.is_member) {
    await router.push({
      name: 'AuthOverview',
      query: { redirect: `/organizations/${orgId}/members` }
    })
    return
  }

  await store.dispatch('org/fetchOrgMembers', orgId)
  await store.dispatch('org/fetchOrgInvites', orgId)
})
</script>

<template>
  <div v-if="org">
    <div v-if="isNewOrg" class="mb-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
      <span class="mt-0.5">⚠</span>
      <span>Add the Primary Contact as <strong>org_admin</strong> here, then set your own permissions or remove entirely.</span>
    </div>

    <div class="mb-2">
      <h2 class="text-lg font-semibold">Total Users: {{totalMembersCount}}</h2>
    </div>

    <Tabs v-model="activeTab">
      <div class="mb-4 flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="organization">Organization ({{membersLength}})</TabsTrigger>
          <TabsTrigger value="project">Project Only ({{projectMembersLength}})</TabsTrigger>
        </TabsList>
        <MembersListInviteDialog
          v-if="activeTab === 'organization' && useCheckOrgAccess(org, 'org_member', ['create'])"
          :org="org"
          @invited="store.dispatch('org/fetchOrgInvites', orgId)"
        />
      </div>

      <TabsContent value="organization">
        <MembersList v-if="membersLength" :members="members" :org="org" :key="members" />

        <div class="mt-6 mb-2 flex items-center">
          <div class="basis-1/2">
            <h3 class="text-base font-bold">Pending invites ({{invitesLength}})</h3>
          </div>
        </div>

        <InvitesList v-if="invitesLength" :invites="invites" :org="org" :key="invites" />
      </TabsContent>

      <TabsContent value="project">
        <p class="mb-4 text-sm text-muted-foreground">
          These users were invited directly to individual projects and are not members of the organization.
        </p>

        <MembersList v-if="projectMembersLength" :members="transformedProjectMembers" :org="org" :key="projectMembers" project-only />
      </TabsContent>
    </Tabs>
  </div>
</template>
